import React, { useState } from 'react';
import {
  FiClock,
  FiCalendar,
  FiFlag,
  FiMoreVertical,
  FiCheckCircle,
  FiCircle,
  FiPlay,
  FiUsers
} from 'react-icons/fi';
import TaskTimer from './TaskTimer';

const TaskListView = ({ tasksByStatus, statusColors, onTaskClick, onStatusChange, onTaskUpdate, canEdit }) => {
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const statusConfig = {
    pending: { title: 'Pending', icon: FiCircle, color: 'text-gray-500' },
    in_progress: { title: 'In Progress', icon: FiPlay, color: 'text-blue-500' },
    completed: { title: 'Completed', icon: FiCheckCircle, color: 'text-green-500' },
    delegated: { title: 'Delegated', icon: FiUsers, color: 'text-purple-500' }
  };

  const priorityBorder = {
    urgent: '#ef4444',
    high: '#f97316',
    medium: '#3b82f6',
    low: '#64748b'
  };

  const priorityBadge = {
    urgent: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    high: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
    medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    low: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  };

  const handleDragOver = (e, status) => {
    if (!canEdit || !draggedTaskId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(status);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverColumn(null);
    }
  };

  const handleDrop = (e, status) => {
    e.preventDefault();
    if (!canEdit || !draggedTaskId) {
      setDragOverColumn(null);
      return;
    }

    const draggedTask = Object.values(tasksByStatus).flat().find(task => task.id === draggedTaskId);
    if (!draggedTask || draggedTask.status === status) {
      setDragOverColumn(null);
      return;
    }

    onStatusChange(draggedTaskId, status);
    setDraggedTaskId(null);
    setDragOverColumn(null);
  };

  const TaskCard = ({ task }) => {
    const StatusIcon = statusConfig[task.status]?.icon || FiCircle;

    const handleDragStart = (e) => {
      if (!canEdit) {
        e.preventDefault();
        return;
      }
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.id);
      requestAnimationFrame(() => {
        setDraggedTaskId(task.id);
      });
    };

    const handleDragEnd = () => {
      setDraggedTaskId(null);
      setDragOverColumn(null);
    };

    return (
      <div
        draggable={canEdit}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={() => onTaskClick(task)}
        className={`group relative p-2.5 rounded-md bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow transition-colors cursor-pointer ${
          canEdit ? 'cursor-grab active:cursor-grabbing' : ''
        } ${draggedTaskId === task.id ? 'opacity-50' : ''}`}
        style={{
          borderLeft: `3px solid ${priorityBorder[task.priority] || priorityBorder.medium}`
        }}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            {/* Status Icon */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (canEdit) {
                  const nextStatus = task.status === 'pending' ? 'in_progress' :
                                   task.status === 'in_progress' ? 'completed' :
                                   task.status === 'completed' ? 'pending' : 'pending';
                  onStatusChange(task.id, nextStatus);
                }
              }}
              className={`mt-0.5 ${statusConfig[task.status]?.color} ${canEdit ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
            >
              <StatusIcon className="w-4 h-4" />
            </button>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-sm text-gray-900 dark:text-white truncate">
                    {task.title}
                  </h4>
                  {task.description && (
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1 mt-0.5">
                      {task.description}
                    </p>
                  )}
                </div>

                {task.priority && (
                  <span className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${priorityBadge[task.priority]}`}>
                    {task.priority === 'medium' ? 'med' : task.priority}
                  </span>
                )}
              </div>

              {/* Metadata Row */}
              <div className="flex items-center justify-between text-xs mt-1.5">
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                  {task.estimated_hours && (
                    <div className="flex items-center gap-1">
                      <FiClock className="w-3 h-3" />
                      <span>{task.estimated_hours}h</span>
                    </div>
                  )}
                </div>

                {task.due_date && (
                  <div className={`flex items-center gap-1 ${
                    new Date(task.due_date) < new Date()
                      ? 'text-red-600 dark:text-red-400'
                      : new Date(task.due_date) < new Date(Date.now() + 24 * 60 * 60 * 1000)
                      ? 'text-orange-600 dark:text-orange-400'
                      : 'text-gray-500 dark:text-gray-400'
                  }`}>
                    <FiCalendar className="w-3 h-3" />
                    <span>{new Date(task.due_date).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {/* Timer */}
              {task.status !== 'completed' && (
                <div className="pt-1.5 mt-1.5 border-t border-gray-100 dark:border-gray-700">
                  <TaskTimer
                    task={task}
                    onTimeUpdate={(taskId, newActualHours) => {
                      if (onTaskUpdate) {
                        onTaskUpdate(taskId, { actual_hours: newActualHours });
                      }
                    }}
                  />
                </div>
              )}

              {/* Tags */}
              {task.tags && task.tags.length > 0 && (
                <div className="flex gap-1 flex-wrap mt-1.5">
                  {task.tags.slice(0, 3).map(tag => (
                    <span
                      key={tag.id}
                      className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                      style={{
                        backgroundColor: `${tag.color}20`,
                        color: tag.color
                      }}
                    >
                      {tag.name}
                    </span>
                  ))}
                  {task.tags.length > 3 && (
                    <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] font-medium text-gray-500 dark:text-gray-400">
                      +{task.tags.length - 3}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onTaskClick(task);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-opacity"
          >
            <FiMoreVertical className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
      {Object.entries(tasksByStatus)
        .filter(([status]) => status !== 'completed')
        .map(([status, tasks]) => {
          const config = statusConfig[status];
          const StatusIcon = config?.icon || FiCircle;
          const draggedTask = draggedTaskId ? Object.values(tasksByStatus).flat().find(task => task.id === draggedTaskId) : null;
          const isDropTarget = dragOverColumn === status && draggedTask && draggedTask.status !== status;

          return (
            <div
              key={status}
              className={`bg-white dark:bg-gray-800 border rounded-md p-3 transition-colors ${
                isDropTarget
                  ? 'border-primary-500 bg-primary-50/30 dark:bg-primary-900/10'
                  : 'border-gray-200 dark:border-gray-700'
              }`}
              onDragOver={(e) => handleDragOver(e, status)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, status)}
            >
              {/* Column Header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <StatusIcon className={`w-4 h-4 ${config?.color}`} />
                  <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                    {config?.title || status}
                  </h3>
                  {isDropTarget && (
                    <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                      Drop here
                    </span>
                  )}
                </div>
                <span className="px-1.5 py-0.5 text-xs rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium">
                  {tasks.length}
                </span>
              </div>

              {/* Tasks */}
              <div className="space-y-2 min-h-[60px]">
                {tasks.map(task => (
                  <TaskCard key={task.id} task={task} />
                ))}

                {tasks.length === 0 && (
                  <div className={`text-center py-8 ${
                    isDropTarget ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 dark:text-gray-500'
                  }`}>
                    <StatusIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-xs font-medium">
                      {isDropTarget ? 'Drop task here' : `No ${config?.title.toLowerCase()} tasks`}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
    </div>
  );
};

export default TaskListView;
